"use client";

import { useState } from "react";
import type { ColorSwatchGroupWithImagesProps } from "../../elements/color-swatch/color-swatch.utils";
import type { PriceProps } from "../../elements/price/price";
import {
  ProductCardD2C,
  type ProductCardD2CBadge,
  type ProductCardD2CProps,
} from "./product-card-d2c";

export interface PlpGtmTrackingData {
  variantCode?: string;
  brand?: string;
  categories?: { code: string; name?: string }[];
  colorName?: string;
  currency?: string;
  priceValue?: number;
  stockStatus?: string;
  itemName?: string;
}

/**
 * Full card state for a single color variant. Keyed by swatch ID in
 * ProductCardD2CClientProps.variantData so the client can swap ALL card fields
 * when the user picks a different color.
 */
export interface VariantCardData {
  image: { src: string };
  name: string;
  href?: string;
  price: PriceProps;
  badges?: ProductCardD2CBadge[];
  productCode: string;
}

export interface ProductCardD2CClientProps
  extends Omit<ProductCardD2CProps, "colorSwatches"> {
  colorSwatches?: ColorSwatchGroupWithImagesProps;
  gtm?: PlpGtmTrackingData;
  /** Full per-variant card data keyed by swatch ID. Populated by the server
   *  adapter; when present, a swatch click updates price, badges, product code,
   *  image, name, and link rather than just the image and name. */
  variantData?: Record<string, VariantCardData>;
}

export function ProductCardD2CClient(
  props: Readonly<ProductCardD2CClientProps>
) {
  const { colorSwatches, image, microcopies, variantData, ...rest } = props;

  const [selectedSwatchId, setSelectedSwatchId] = useState(
    colorSwatches?.selectedId ?? colorSwatches?.swatches[0]?.id
  );

  const selectedSwatch = colorSwatches?.swatches.find(
    (s) => s.id === selectedSwatchId
  );
  const selectedVariant =
    selectedSwatchId !== undefined
      ? variantData?.[selectedSwatchId]
      : undefined;

  const displayedImageSrc =
    selectedVariant?.image.src ?? selectedSwatch?.imageUrl ?? image.src;
  const displayedName =
    selectedVariant?.name ?? selectedSwatch?.name ?? microcopies.name;
  const displayedHref =
    selectedVariant?.href ?? selectedSwatch?.href ?? rest.href;
  const displayedPrice = selectedVariant?.price ?? rest.price;
  const displayedBadges = selectedVariant?.badges ?? rest.badges;
  const displayedProductCode =
    selectedVariant?.productCode ?? microcopies.productCode;

  return (
    <ProductCardD2C
      {...rest}
      badges={displayedBadges}
      colorSwatches={
        colorSwatches
          ? {
              ...colorSwatches,
              swatches: colorSwatches.swatches.map(
                ({ imageUrl: _, name: __, href: ___, ...s }) => s
              ),
              selectedId: selectedSwatchId,
              onSelect: setSelectedSwatchId,
            }
          : undefined
      }
      href={displayedHref}
      image={{ ...image, src: displayedImageSrc }}
      microcopies={{
        ...microcopies,
        name: displayedName,
        productCode: displayedProductCode,
      }}
      price={displayedPrice}
    />
  );
}
